/**
 * Wishlist Cookie Service
 *
 * Cookie format (URI-encoded JSON array, 90-day expiry, `path=/`) is
 * intentionally identical to the classic Frontend implementation in
 * `Frontend/src/js/wishlist.js` so the same cookie is readable across
 * Pimcore-rendered pages. The Vue app stores `productCode` (NBC) values;
 * the classic Frontend historically stored Pimcore numeric IDs.
 */

const COOKIE_NAME = 'wishlist';
const EXPIRY_DAYS = 90;

export class WishlistCookieService {
  static readonly KEY = COOKIE_NAME;

  /**
   * Read product codes from the wishlist cookie. Returns an empty array
   * if the cookie is missing, malformed, or storage is unavailable.
   */
  static read(): string[] {
    try {
      const match = document.cookie.match(new RegExp('(^| )' + COOKIE_NAME + '=([^;]+)'));
      if (!match) return [];
      const parsed: unknown = JSON.parse(decodeURIComponent(match[2]));
      if (!Array.isArray(parsed)) return [];
      return parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
    } catch (error) {
      console.warn('[WishlistCookieService] Failed to read cookie:', error);
      return [];
    }
  }

  /**
   * Write product codes to the wishlist cookie. Silently no-ops on
   * failure (e.g. cookies disabled).
   */
  static write(productCodes: string[]): void {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      const value = encodeURIComponent(JSON.stringify(productCodes));
      document.cookie = `${COOKIE_NAME}=${value};expires=${expires.toUTCString()};path=/`;
    } catch (error) {
      console.warn('[WishlistCookieService] Failed to write cookie:', error);
    }
  }
}
